import { amorphicStatic, supertypeClass } from '@haventech/amorphic';
import { AgentLicense } from '../../../../../common/js/agent/AgentLicense';
import { Agent } from '../../../../../common/js/customer/Agent';
import { PolicyAgent } from '../../../../../common/js/customer/PolicyAgent';
import { SuitabilityPayloadUtils } from '../../../../../common/js/questionnaireService/annuity/QSPayloadUtils';
import { QsAPIGetQuestionnaireResponse } from '../../../../../common/js/questionnaireService/QuestionnaireServiceTypes';
import { Utils } from '../../../../../common/js/Utils';
import { CoverpathAnnuity } from '../../../../classes/coverpath_subclasses/CoverpathAnnuity';
import {
  AnnuityAdditionalSigners,
  AnnuityApplicationMetadataFileName,
  AnnuitySuitabilityMetadataFileName,
  AnnuityProductSelection,
} from '../../../interfaces/CoverpathTypes';
import { AnnuityDocuments } from '../../../static/annuity-app-package/AnnuityDocuments';
import {
  addAddressToValues,
  addCitizenshipToValues,
  addFullNameToValues,
  addGenderToValues,
  addMultiLineTextBoxValues,
  addMultiLineTextBoxValuesPerCharacter,
  addPhoneNumberToValues,
  getFullName,
  removeUndefinedValuesFromObj,
  truncateLineTextBoxValue,
} from './common';
import { addSuitabilityValues } from './F9726S';
import { addSuitabilityValues20210625 } from './F9726S-20210625';
import { addSuitabilityBIValues } from './F9726S-BI';
import { addSuitabilityBIValues20210625 } from './F9726S-BI-20210625';
import { addApplicationValues } from './ICC14A1300-AN';
import { addApplicationValues as addApplicationValues20210691 } from './ICC21A1300-AN-20210601';
import { addSuitabilityBIValues20210901 } from './F9726S-BI-20210901';
import { CommonUtilsBackend } from '../../../../../common/js/static/CommonUtilsBackend';
import { POLICY_DOC_DOCTYPE } from '../../../../../common/js/constants/policy-doc';
import { addLargeCaseQuestionnaireValues } from './FR2302-v2019';
import { QuestionnaireServiceUtils } from '../../../../../common/js/questionnaireService/QuestionnaireServiceUtils';
import { addSuitabilityBIValues20220201 } from './F9726S-BI-20220201';
import { addAnnuityQualifiedPlanRolloverValues } from './FR3013-20220201';
import { LoggerSingleton, Category, PiiLevelEnum } from '../../../../../common/js/logger/LoggerSingleton';

export interface PDFMappingField {
  _comment: string;
  x: number;
  y: number;
  text: string;
  type: string;
  fontSize: number;
}

export interface PDFMappingFills {
  page: number;
  fill: PDFMappingField[];
}

export interface PDFMappingObject {
  inheritMapping?: boolean;
  formType: string;
  fills: PDFMappingFills[];
  buffer: any;
}

export interface PDFMappingDataObject {
  data: PDFMappingObject[];
}

@supertypeClass({ toClient: false })
export class AnnuityPDFUtils {
  public static async getPDFMetadataContents(filePath: string): Promise<PDFMappingObject> {
    const metadataFileContents = await CommonUtilsBackend.readFile(filePath);
    return JSON.parse(String(metadataFileContents)) as PDFMappingObject;
  }

  /**
   *
   * @param product
   * @param legalState
   * @param docType
   * @param metadataFileName optional param to override the app package name
   */
  public static async getPDFMetadata(
    product: AnnuityProductSelection,
    legalState: string,
    docType: string,
    metadataFileName?: string
  ): Promise<PDFMappingObject> {
    const document = await AnnuityDocuments.getAnnuityDoc(product, legalState, docType);
    const documentName = !!metadataFileName ? metadataFileName : document.name;

    LoggerSingleton.getInstance().info(
      {
        module: 'AnnuityPDFUtils',
        function: 'getPDFMetadata',
        data: {
          legalState,
          docType,
          documentName,
        },
        category: Category.request,
      },
      `Retrieving pdf and metadata`
    );

    const fileContents = await CommonUtilsBackend.readFile(
      `apps/coverpath-common/pdfs/annuity/${documentName}.pdf`,
      'base64'
    );

    const metadata = await this.getPDFMetadataContents(
      `apps/coverpath-common/pdfs/annuity/mapping/${documentName}.json`
    );

    if (metadata.inheritMapping) {
      const parentMetadata = await this.getPDFMetadataContents(
        `apps/coverpath-common/pdfs/annuity/mapping/${metadata.inheritMapping}.json`
      );
      this.handleInheritedMapping(parentMetadata, metadata, fileContents);
      return parentMetadata;
    }

    metadata.buffer = fileContents;
    return metadata;
  }

  /**
   * For pdf mappings that is using an inherited parent json
   * NOTE - when inheriting, there is no field override functionality
   * TODO - add support for overrides
   * @param parentMetadata
   * @param childMetadata
   * @param fileContents
   */
  private static handleInheritedMapping(
    parentMetadata: PDFMappingObject,
    childMetadata: PDFMappingObject,
    fileContents: any
  ): void {
    parentMetadata.buffer = fileContents;
    const parentPageMap = this.getPageMap(parentMetadata);
    const parentFills = parentMetadata.fills;
    const fills = childMetadata.fills;
    const len = fills.length;

    for (let i = 0; i < len; i++) {
      const metadataForPage = fills[i];
      const page = metadataForPage.page;
      if (parentPageMap.has(page)) {
        // append all fields to existing page
        const parentPage = parentPageMap.get(page);
        for (const field of metadataForPage.fill) {
          parentPage.push(field);
        }
      } else {
        // page does not exist, so add a new page to parent metadata
        parentFills.push(metadataForPage);
      }
    }
  }

  /**
   * Create a map from page number to page for faster and easier access
   * @param metadata
   */
  private static getPageMap(metadata: PDFMappingObject): Map<number, PDFMappingField[]> {
    const pageMap = new Map<number, PDFMappingField[]>();
    const fills = metadata.fills;
    for (const entry of fills) {
      pageMap.set(entry.page, entry.fill);
    }
    return pageMap;
  }

  public static fillMetadata(values: object, metadata: PDFMappingObject): void {
    for (let i = 0; i < metadata.fills.length; i++) {
      const metadataForPage = metadata.fills[i];

      const fields = metadataForPage.fill;

      for (let j = 0; j < fields.length; j++) {
        const field = fields[j];

        if (!field.text) {
          field.text = '';
        }
        if (!field.type) {
          field.type = 'text';
        }

        if (!field.fontSize) {
          field.fontSize = 11;
        }

        const key = field._comment;
        const value = values[key];

        if (value !== undefined && value !== null) {
          field.text = value;
        }
      }
    }
  }

  public static async getClientSignatureInput(
    annuity: CoverpathAnnuity,
    docType: string,
    name: string,
    additionalSigner?: AnnuityAdditionalSigners
  ): Promise<PDFMappingDataObject> {
    return this.getSignatureInput(annuity, docType, name, true, additionalSigner);
  }

  public static async getAgentSignatureInput(
    annuity: CoverpathAnnuity,
    docType: string,
    name: string
  ): Promise<PDFMappingDataObject> {
    return this.getSignatureInput(annuity, docType, name, false);
  }

  private static async getSignatureInput(
    annuity: CoverpathAnnuity,
    docType: string,
    name: string,
    isClient: boolean,
    additionalSigner?: AnnuityAdditionalSigners
  ): Promise<PDFMappingDataObject> {
    let documentName = (
      await AnnuityDocuments.getAnnuityDoc(
        annuity.product as AnnuityProductSelection,
        annuity.getApplicationState(),
        docType
      )
    ).name;

    // application and suitability form names should not be derived from app package definitions since they are controlled by feature flag
    // we check for the name associated with the policy
    if (docType === POLICY_DOC_DOCTYPE.annuityApplication) {
      const qsResponse = await QuestionnaireServiceUtils.getQuestionnaire(
        annuity.annuityApplication.applicationFormQsID
      );
      documentName = qsResponse.metadata_file_name;
    } else if (docType === POLICY_DOC_DOCTYPE.annuitySuitabilityForm) {
      const qsResponse = await QuestionnaireServiceUtils.getQuestionnaire(annuity.suitabilityFormQsID);
      documentName = qsResponse.metadata_file_name;
    }

    let folder = additionalSigner ? additionalSigner : isClient ? 'client' : 'agent';
    if (docType === POLICY_DOC_DOCTYPE.annuityCertificateOfTrustForm && annuity.trustOrOtherEntity) {
      const {
        type,
        authorizedTrusteeList: [trustee],
      } = annuity.trustOrOtherEntity;
      if (type === 'trust' && trustee.type === 'entity') {
        folder = 'trustee-entity';
      }
    }

    const file = `apps/coverpath-common/pdfs/annuity/mapping/signature/${folder}/${documentName}.json`;
    LoggerSingleton.getInstance().info(
      {
        module: 'AnnuityPDFUtils',
        function: 'getSignatureInput',
        context: {
          piiLevel: PiiLevelEnum.s2,
        },
        data: {
          contractNumber: annuity.contractNumber,
          docType,
          name,
          isClient,
          additionalSigner,
          file,
        },
        category: Category.request,
      },
      'Loading mapping file'
    );
    const fileContents = await CommonUtilsBackend.readFile(file);

    const signatureMetadata = JSON.parse(String(fileContents)) as PDFMappingDataObject;
    const mappingObject = signatureMetadata.data[0];

    for (let i = 0; i < mappingObject.fills.length; i++) {
      const metadataForPage = mappingObject.fills[i];
      const fields = metadataForPage.fill;

      for (let j = 0; j < fields.length; j++) {
        const field = fields[j];
        const key = field._comment;
        if (
          key === 'client signature' ||
          key === 'client name' ||
          key === 'agent signature' ||
          key === 'agent name' ||
          key === 'signature' ||
          key === 'name'
        ) {
          field.text = name;
        } else if (key === 'client city state signed') {
          field.text = `${annuity.getApplicationCity()}, ${annuity.getApplicationState()}`;
        }
      }
    }

    return signatureMetadata;
  }
}

function addOwnerValuesForPDF(values: object): void {
  const ownership = values['owner.primary_ownership'];
  values[`owner.primary_ownership:${ownership}`] = 'X';

  if (ownership === 'individual') {
    addFullNameToValues(values, `owner.name`);
  } else {
    values['owner.gender'] = 'Entity';
  }

  addGenderToValues(values, `owner`);
  addAddressToValues(values, `owner.address_legal`);

  if (values[`owner.mailing_same_as_legal`] === 'No') {
    addAddressToValues(values, `owner.address_mailing`);
  }

  addPhoneNumberToValues(values, `owner.phone`);
  addPhoneNumberToValues(values, `owner.phone_alternate`);
  addCitizenshipToValues(values, `owner`);
}

function addJointOwnerValuesForPDF(values: object): void {
  const hasJointOwner = values['owner.is_there_a_joint_owner'] === 'Yes';
  if (hasJointOwner) {
    addFullNameToValues(values, `owner.joint_owner_details.name`);
    addGenderToValues(values, `owner.joint_owner_details`);
    addAddressToValues(values, `owner.joint_owner_details.address_legal`);

    if (values[`owner.joint_owner_details.mailing_same_as_legal`] === 'No') {
      addAddressToValues(values, `owner.joint_owner_details.address_mailing`);
    }

    addPhoneNumberToValues(values, `owner.joint_owner_details.phone`);
    addCitizenshipToValues(values, `owner.joint_owner_details`);
  }
}

function addAnnuitantValuesForPDF(values: object) {
  const isAnnuitantOwner = values['annuitant.is_annuitant_owner'];
  values[`annuitant.is_annuitant_owner:${isAnnuitantOwner}`] = 'X';
  if (isAnnuitantOwner === 'No') {
    addFullNameToValues(values, 'annuitant.annuitant_details.name');
    addGenderToValues(values, 'annuitant.annuitant_details');
    addAddressToValues(values, 'annuitant.annuitant_details.address_legal');

    if (values['annuitant.annuitant_details.mailing_same_as_legal'] === 'No') {
      addAddressToValues(values, 'annuitant.annuitant_details.address_mailing');
    }

    addPhoneNumberToValues(values, 'annuitant.annuitant_details.phone');
  }
}

function addJointAnnuitantValuesForPDF(values: object) {
  //can be Yes/No/Undefined
  //Undefined means no Joint Annuitant (for 2021 EOY)
  const jointOwnerIsJointAnnuitant = values['annuitant.joint_owner_is_joint_annuitant'];
  if (!!jointOwnerIsJointAnnuitant) {
    values[`annuitant.joint_owner_is_joint_annuitant:${jointOwnerIsJointAnnuitant}`] = 'X';

    if (jointOwnerIsJointAnnuitant === 'No') {
      addFullNameToValues(values, 'annuitant.joint_annuitant_details.name');
      addGenderToValues(values, 'annuitant.joint_annuitant_details');
      addAddressToValues(values, 'annuitant.joint_annuitant_details.address_legal');

      if (values['annuitant.joint_annuitant_details.mailing_same_as_legal'] === 'No') {
        addAddressToValues(values, 'annuitant.joint_annuitant_details.address_mailing');
      }

      addPhoneNumberToValues(values, 'annuitant.joint_annuitant_details.phone');
    }
  }
}

function addServicingAgentValuesForPDF(values: object, servicingAgent: Agent) {
  values['servicingAgent.name.fullName'] = servicingAgent.person.fullName;
  values['servicingAgent.name.firstName'] = servicingAgent.person.firstName;
  values['servicingAgent.name.lastName'] = servicingAgent.person.lastName;
  values['servicingAgent.agency.name'] = servicingAgent.agencyName;
}

/**
 * Contains all functionality for dynamically adding values to annuity pdfs
 */
export class QSValuesPDFTransformer {
  public static addDynamicValuesForPDF(
    metadata_file_name: string,
    values: object,
    qsResponse: QsAPIGetQuestionnaireResponse,
    policyAgents?: Array<PolicyAgent>,
    servicingAgent?: Agent
  ): void {
    switch (metadata_file_name) {
      case AnnuityApplicationMetadataFileName.ICC14A1300_AN: {
        return addApplicationValues(values, qsResponse, policyAgents, servicingAgent);
      }
      case AnnuityApplicationMetadataFileName.ICC21A1300_AN_20210601: {
        return addApplicationValues20210691(values, qsResponse, policyAgents, servicingAgent);
      }
      case AnnuitySuitabilityMetadataFileName.F9726S: {
        return addSuitabilityValues(values, qsResponse);
      }
      case AnnuitySuitabilityMetadataFileName.F9726S_BI: {
        return addSuitabilityBIValues(values, qsResponse);
      }
      case AnnuitySuitabilityMetadataFileName.F9726S_20210625: {
        return addSuitabilityValues20210625(values, qsResponse);
      }
      case AnnuitySuitabilityMetadataFileName.F9726S_BI_20210625: {
        return addSuitabilityBIValues20210625(values, qsResponse);
      }
      case AnnuitySuitabilityMetadataFileName.F9726S_BI_20210901: {
        return addSuitabilityBIValues20210901(values, qsResponse);
      }
      case AnnuitySuitabilityMetadataFileName.F9726S_BI_20220201: {
        return addSuitabilityBIValues20220201(values, qsResponse);
      }
      default: {
        throw new Error(`Unsupported form ${metadata_file_name}`);
      }
    }
  }
}

export class QLACValuesPDFTransformer {
  public static addDynamicValuesForPDF(filledValues: object, persistedAnnuity: CoverpathAnnuity) {
    QLACValuesPDFTransformer.addOwnerValuesForQLACPDF(filledValues, persistedAnnuity);
  }

  public static addOwnerValuesForQLACPDF(filledValues: object, persistedAnnuity: CoverpathAnnuity) {
    filledValues['appliedForAnnuity.owner_full_name'] = persistedAnnuity.owner.getFullNameOfOwner();
  }
}

export class MM2DisclosureValuesPDFTransformer {
  public static addDynamicValuesForPDF(filledValues: object, persistedAnnuity: CoverpathAnnuity) {
    MM2DisclosureValuesPDFTransformer.addOwnerValuesForMM2DisclosurePDF(filledValues, persistedAnnuity);
  }

  public static addOwnerValuesForMM2DisclosurePDF(filledValues: object, persistedAnnuity: CoverpathAnnuity) {
    filledValues['owner.name.fullName'] = persistedAnnuity.owner.getFullNameOfOwner();
    filledValues['owner.address_legal.line1.format'] = persistedAnnuity.owner.legalAddress.getLine1();
    filledValues['owner.address_legal.line2.format'] = persistedAnnuity.owner.legalAddress.getLine2();

    if (filledValues['annuitant.is_annuitant_owner'] === 'Yes') {
      filledValues['annuitant.fullName'] = persistedAnnuity.owner.getFullNameOfOwner();
      filledValues['annuitant.ssn'] = filledValues['owner.tax_ID'];
    } else {
      filledValues['annuitant.fullName'] = persistedAnnuity.annuitant.fullName;
      filledValues['annuitant.ssn'] = filledValues['annuitant.annuitant_details.tax_ID'];
    }
  }
}

export class LargeCaseQuestionnaireValuesPDFTransformer {
  public static addDynamicValuesForPDF(filledValues: object, persistedAnnuity: CoverpathAnnuity) {
    addOwnerValuesForPDF(filledValues);
    addJointOwnerValuesForPDF(filledValues);
    addAnnuitantValuesForPDF(filledValues);
    addJointAnnuitantValuesForPDF(filledValues);
    addLargeCaseQuestionnaireValues(filledValues, persistedAnnuity);
  }
}

export class AnnuityQualifiedPlanRolloverValuesPDFTransformer {
  public static addDynamicValuesForPDF(filledValues: object, persistedAnnuity: CoverpathAnnuity) {
    addOwnerValuesForPDF(filledValues);
    addJointOwnerValuesForPDF(filledValues);
    addAnnuitantValuesForPDF(filledValues);
    addJointAnnuitantValuesForPDF(filledValues);
    addAnnuityQualifiedPlanRolloverValues(filledValues, persistedAnnuity);
  }
}

export class DisclosureAcknowledgmentValuesPDFTransformer {
  public static addDynamicValuesForPDF(filledValues: object, persistedAnnuity: CoverpathAnnuity) {
    addOwnerValuesForPDF(filledValues);
    addJointOwnerValuesForPDF(filledValues);
    addAnnuitantValuesForPDF(filledValues);
    addJointAnnuitantValuesForPDF(filledValues);
    addServicingAgentValuesForPDF(filledValues, persistedAnnuity.servicingAgent);
  }
}

export class ReplacementValuesPDFTransformer {
  public static addDynamicValuesForPDF(
    legalState: string,
    filledValues: object,
    persistedAnnuity: CoverpathAnnuity,
    unpersistedAnnuityWithApplicationValues: CoverpathAnnuity,
    qsSuitabilityResponse: QsAPIGetQuestionnaireResponse,
    primaryPolicyAgent: PolicyAgent,
    primaryProducerLicense: AgentLicense
  ) {
    this.addSuitabilityValuesForReplacementPDF(filledValues, qsSuitabilityResponse);
    this.addApplicationValuesForReplacementPDF(filledValues, unpersistedAnnuityWithApplicationValues);
    this.addOwnerValuesForReplacementPDF(filledValues, unpersistedAnnuityWithApplicationValues);
    this.addProducerValuesForReplacementPDF(filledValues, primaryPolicyAgent, primaryProducerLicense);
    this.addReplacementContractValuesForReplacementPDF(filledValues, qsSuitabilityResponse);

    // Logic excluding 5436 forms
    if (!['DE', 'GA', 'IL', 'IN', 'KS', 'MA', 'MN', 'NV', 'OK', 'PA', 'TN', 'WA', 'WY'].includes(legalState)) {
      this.add6487ValuesForReplacementPDF(filledValues);
      this.add6487_809ValuesForReplacementPDF(filledValues);
    }

    // Align the text and adjust for overflow text in the tables
    // Also some state specific value adjustments
    if (
      [
        'AL',
        'AK',
        'AZ',
        'CO',
        'CT',
        'IA',
        'KY',
        'LA',
        'ME',
        'MD',
        'MS',
        'MO',
        'MT',
        'NE',
        'NH',
        'NM',
        'OH',
        'OR',
        'SC',
        'TX',
        'VT',
        'VA',
        'WV',
        'WI',
        'NJ', // sperate from the 6487-US
      ].includes(legalState)
    ) {
      this.align6487ReplacementContractValues(filledValues, qsSuitabilityResponse);
    } else if ('GA' === legalState) {
      this.addGAValuesForReplacementPDF(filledValues);
      this.alignGAReplacementContractValues(filledValues, qsSuitabilityResponse);
    } else if ('IN' === legalState) {
      this.alignINReplacementContractValues(filledValues, qsSuitabilityResponse);
    } else if ('MA' === legalState) {
      this.addMAValuesForReplacementPDF(filledValues);
      this.alignMAReplacementContractValues(filledValues, qsSuitabilityResponse);
    } else if ('KS' === legalState) {
      this.addKSValuesForReplacementPDF(filledValues, qsSuitabilityResponse);
      this.alignKSReplacementContractValues(filledValues, qsSuitabilityResponse);
    } else if ('WY' === legalState) {
      this.alignWYReplacementContractValues(filledValues, qsSuitabilityResponse);
    } else if ('DE' === legalState) {
      this.alignDEReplacementContractValues(filledValues, qsSuitabilityResponse);
    } else if ('MN' === legalState) {
      this.alignMNReplacementContractValues(filledValues, qsSuitabilityResponse);
    } else if ('OK' === legalState) {
      this.alignOKReplacementContractValues(filledValues, qsSuitabilityResponse);
    } else if ('TN' === legalState) {
      this.alignTNReplacementContractValues(filledValues, qsSuitabilityResponse);
    } else if ('PA' === legalState) {
      this.alignPAReplacementContractValues(filledValues, qsSuitabilityResponse);
    } else if ('WA' === legalState) {
      this.addWAValuesForReplacementPDF(filledValues);
      this.alignWAReplacementContractValues(filledValues, qsSuitabilityResponse);
    } else if (['HI', 'NC', 'UT'].includes(legalState)) {
      this.align809ReplacementContractValues(filledValues, qsSuitabilityResponse);
    } else if ('RI' === legalState) {
      this.addRIValuesForReplacementPDF(filledValues, qsSuitabilityResponse);
    } else if ('NV' === legalState) {
      this.addNVValuesForReplacementPDF(filledValues);
    } else if ('AR' === legalState) {
      this.align6487ReplacementContractValues(filledValues, qsSuitabilityResponse);
      this.addARValuesForReplacementPDF(filledValues);
    } else if ('IL' === legalState) {
      this.alignILReplacementContractValues(filledValues, qsSuitabilityResponse);
    } else if ('ID' === legalState) {
      this.alignIDReplacementContractValues(filledValues, qsSuitabilityResponse);
    }

    // This is there to remove undefined values from the object.
    // Needed because there is no validation on replacemen form.
    removeUndefinedValuesFromObj(filledValues);
  }

  public static addMAValuesForReplacementPDF(filledValues: object) {
    const requestYieldIndices = filledValues['replacementForm.F5436-MA--request_yield_indices'];
    if (requestYieldIndices) {
      filledValues['replacementForm.F5436-MA--request_yield_indices:true'] = 'X';
    }
  }

  public static addGAValuesForReplacementPDF(filledValues: object) {
    const requestYieldIndices = filledValues['replacementForm.F5436-GA--summary_statement'];
    if (requestYieldIndices) {
      filledValues['replacementForm.F5436-GA--summary_statement:true'] = 'X';
    }
  }

  public static addWAValuesForReplacementPDF(filledValues: object) {
    const reducedBenefits = filledValues['replacementForm.F5436-WA--reduced_benefits'];
    filledValues[`replacementForm.F5436-WA--reduced_benefits:${reducedBenefits}`] = 'X';

    const penaltiesNewPolicy = filledValues['replacementForm.F5436-WA--penalties_new_policy'];
    filledValues[`replacementForm.F5436-WA--penalties_new_policy:${penaltiesNewPolicy}`] = 'X';

    const penaltiesExistingPolicy = filledValues['replacementForm.F5436-WA--penalties_existing_policy'];
    filledValues[`replacementForm.F5436-WA--penalties_existing_policy:${penaltiesExistingPolicy}`] = 'X';

    const taxConsequences = filledValues['replacementForm.F5436-WA--tax_consequences'];
    filledValues[`replacementForm.F5436-WA--tax_consequences:${taxConsequences}`] = 'X';

    const interestEarning = filledValues['replacementForm.F5436-WA--interest_earning'];
    filledValues[`replacementForm.F5436-WA--interest_earning:${interestEarning}`] = 'X';

    const minimumAmount = filledValues['replacementForm.F5436-WA--minimum_amount'];
    filledValues[`replacementForm.F5436-WA--minimum_amount:${minimumAmount}`] = 'X';

    const interestQuotedWhen = filledValues['replacementForm.F5436-WA--interest_quoted_when'];
    //check before or after
    filledValues[`replacementForm.F5436-WA--interest_quoted_when:${interestQuotedWhen}`] = 'X';

    const effectsOfReplacing = filledValues['replacementForm.F5436-WA--effects_of_replacing'];
    filledValues[`replacementForm.F5436-WA--effects_of_replacing:${effectsOfReplacing}`] = 'X';
  }

  public static addKSValuesForReplacementPDF(
    filledValues: object,
    qsSuitabilityResponse: QsAPIGetQuestionnaireResponse
  ) {
    let internalCount = 1;
    let externalCount = 1;

    const replacedContracts = SuitabilityPayloadUtils.getReplacedContracts(qsSuitabilityResponse);

    for (let i = 1; i <= replacedContracts.length; i++) {
      const insurer = filledValues[`replacementForm.contract_${i}_insurer`];
      const annuitantFullName = filledValues[`replacementForm.contract_${i}_annuitant.fullName`];
      const contractNumber = filledValues[`replacementForm.contract_${i}_number`];
      const faceAmount = filledValues[`replacementForm.contract_${i}_face_amount`];
      const typeOfPolicy = filledValues[`replacementForm.contract_${i}_type_of_policy`];

      const internalOrExternal = filledValues[`replacementForm.contract_${i}_internal_or_external`].toLowerCase();
      const countToUse = internalOrExternal === 'internal' ? internalCount : externalCount;

      filledValues[`replacementForm.contract_${countToUse}_insurer.${internalOrExternal}`] = insurer;
      filledValues[`replacementForm.contract_${countToUse}_annuitant.fullName.${internalOrExternal}`] =
        annuitantFullName;
      filledValues[`replacementForm.contract_${countToUse}_number.${internalOrExternal}`] = contractNumber;
      filledValues[`replacementForm.contract_${countToUse}_face_amount.${internalOrExternal}`] = faceAmount;
      filledValues[`replacementForm.contract_${countToUse}_type_of_policy.${internalOrExternal}`] = typeOfPolicy;

      if (internalOrExternal === 'internal') {
        internalCount++;
      } else {
        externalCount++;
      }
    }
  }

  public static addRIValuesForReplacementPDF(
    filledValues: object,
    qsSuitabilityResponse: QsAPIGetQuestionnaireResponse
  ) {
    const replacedContracts = SuitabilityPayloadUtils.getReplacedContracts(qsSuitabilityResponse);

    //saving this in case we have an internal replacement
    const initialPurchaseAmount = filledValues['appliedForAnnuity.initial_purchase_amount'];
    filledValues['appliedForAnnuity.initial_purchase_amount'] = '';
    for (let i = 1; i <= replacedContracts.length; i++) {
      const isExternal = filledValues[`replacementForm.contract_${i}_internal_or_external`] === 'External';

      // remove death benefits when contract is external.
      if (isExternal) {
        filledValues[`replacementForm.contract_${i}_death_benefit`] = '';
      } else {
        // Re-add initial purchase amount if any of the replacements are internal
        filledValues['appliedForAnnuity.initial_purchase_amount'] = initialPurchaseAmount;
      }
    }
  }

  public static addNVValuesForReplacementPDF(filledValues: object) {
    addMultiLineTextBoxValues(filledValues, 'replacementForm.contract_1_insurer', 158, 298, 5.5);
    addMultiLineTextBoxValuesPerCharacter(filledValues, 'replacementForm.contract_1_number', 158, 298, 5.5);
  }

  public static addARValuesForReplacementPDF(filledValues: object) {
    addFullNameToValues(filledValues, 'replacementForm.contract_1_owner_name');
    addFullNameToValues(filledValues, 'replacementForm.contract_1_joint_owner_name');
    addFullNameToValues(filledValues, 'replacementForm.contract_1_annuitant');
  }

  public static align6487ReplacementContractValues(
    filledValues: object,
    qsSuitabilityResponse: QsAPIGetQuestionnaireResponse
  ) {
    const replacedContracts = SuitabilityPayloadUtils.getReplacedContracts(qsSuitabilityResponse);

    for (let i = 0; i < replacedContracts.length; i++) {
      truncateLineTextBoxValue(filledValues, `replacementForm.contract_${i + 1}_insurer`, 50, 198, 5.5);
      truncateLineTextBoxValue(filledValues, `replacementForm.contract_${i + 1}_number`, 201, 318, 5.5);
      truncateLineTextBoxValue(filledValues, `replacementForm.contract_${i + 1}_annuitant.fullName`, 320, 439, 5.5);
    }
  }

  public static alignGAReplacementContractValues(
    filledValues: object,
    qsSuitabilityResponse: QsAPIGetQuestionnaireResponse
  ) {
    const replacedContracts = SuitabilityPayloadUtils.getReplacedContracts(qsSuitabilityResponse);

    for (let i = 0; i < replacedContracts.length; i++) {
      truncateLineTextBoxValue(filledValues, `replacementForm.contract_${i + 1}_insurer`, 27, 152, 5.5);
      truncateLineTextBoxValue(filledValues, `replacementForm.contract_${i + 1}_plan_name`, 158, 309, 5.5);
      truncateLineTextBoxValue(filledValues, `replacementForm.contract_${i + 1}_face_amount`, 319, 405, 5.5);
      truncateLineTextBoxValue(filledValues, `replacementForm.contract_${i + 1}_number`, 508, 586, 5.5);
    }
  }

  public static alignPAReplacementContractValues(
    filledValues: object,
    qsSuitabilityResponse: QsAPIGetQuestionnaireResponse
  ) {
    const replacedContracts = SuitabilityPayloadUtils.getReplacedContracts(qsSuitabilityResponse);

    for (let i = 0; i < replacedContracts.length; i++) {
      truncateLineTextBoxValue(filledValues, `replacementForm.contract_${i + 1}_number`, 27, 137, 5.5);
      truncateLineTextBoxValue(filledValues, `replacementForm.contract_${i + 1}_insurer`, 141, 251, 5.5);
      truncateLineTextBoxValue(filledValues, `replacementForm.contract_${i + 1}_annuitant.fullName`, 254, 363, 5.5);
      truncateLineTextBoxValue(filledValues, `replacementForm.contract_${i + 1}_plan_name`, 366, 475, 5.5);
      truncateLineTextBoxValue(filledValues, `replacementForm.contract_${i + 1}_face_amount`, 479, 588, 5.5);
    }
  }

  public static alignWAReplacementContractValues(
    filledValues: object,
    qsSuitabilityResponse: QsAPIGetQuestionnaireResponse
  ) {
    addMultiLineTextBoxValues(filledValues, `replacementForm.F5436-WA--penalties_new_policy_explain`, 35, 590, 5.5);
    addMultiLineTextBoxValues(
      filledValues,
      `replacementForm.F5436-WA--penalties_existing_policy_explain`,
      35,
      590,
      5.5
    );
    addMultiLineTextBoxValues(filledValues, `replacementForm.F5436-WA--tax_consequences_explain`, 35, 590, 5.5);
    addMultiLineTextBoxValues(filledValues, `replacementForm.F5436-WA--interest_earning_explain`, 48, 590, 5.5);

    addMultiLineTextBoxValues(filledValues, `replacementForm.F5436-WA--minimum_amount_explain`, 35, 590, 5.5);

    const replacedContracts = SuitabilityPayloadUtils.getReplacedContracts(qsSuitabilityResponse);

    for (let i = 0; i < replacedContracts.length; i++) {
      truncateLineTextBoxValue(filledValues, `replacementForm.contract_${i + 1}_annuitant`, 20, 269, 5.5);
      truncateLineTextBoxValue(filledValues, `replacementForm.contract_${i + 1}_insurer`, 268, 389, 5.5);
      truncateLineTextBoxValue(filledValues, `replacementForm.contract_${i + 1}_number`, 388, 462, 5.5);
      truncateLineTextBoxValue(filledValues, `replacementForm.contract_${i + 1}_face_amount`, 462, 356, 5.5);
      truncateLineTextBoxValue(filledValues, `replacementForm.contract_${i + 1}_plan_name`, 534, 595, 5.5);
    }
  }

  public static alignOKReplacementContractValues(
    filledValues: object,
    qsSuitabilityResponse: QsAPIGetQuestionnaireResponse
  ) {
    const replacedContracts = SuitabilityPayloadUtils.getReplacedContracts(qsSuitabilityResponse);

    for (let i = 0; i < replacedContracts.length; i++) {
      truncateLineTextBoxValue(filledValues, `replacementForm.contract_${i + 1}_insurer`, 27, 152, 5.5);
      truncateLineTextBoxValue(filledValues, `replacementForm.contract_${i + 1}_annuitant.fullName`, 158, 310, 5.5);
      truncateLineTextBoxValue(filledValues, `replacementForm.contract_${i + 1}_number`, 316, 409, 5.5);
      truncateLineTextBoxValue(filledValues, `replacementForm.contract_${i + 1}_plan_name`, 413, 503, 5.5);
      truncateLineTextBoxValue(filledValues, `replacementForm.contract_${i + 1}_face_amount`, 510, 588, 5.5);
    }
  }

  public static alignTNReplacementContractValues(
    filledValues: object,
    qsSuitabilityResponse: QsAPIGetQuestionnaireResponse
  ) {
    const replacedContracts = SuitabilityPayloadUtils.getReplacedContracts(qsSuitabilityResponse);

    for (let i = 0; i < replacedContracts.length; i++) {
      truncateLineTextBoxValue(filledValues, `replacementForm.contract_${i + 1}_insurer`, 27, 149, 5.5);
      truncateLineTextBoxValue(filledValues, `replacementForm.contract_${i + 1}_annuitant.fullName`, 158, 309, 5.5);
      truncateLineTextBoxValue(filledValues, `replacementForm.contract_${i + 1}_number`, 316, 405, 5.5);
      truncateLineTextBoxValue(filledValues, `replacementForm.contract_${i + 1}_face_amount`, 413, 502, 5.5);
      truncateLineTextBoxValue(filledValues, `replacementForm.contract_${i + 1}_type_of_policy`, 510, 584, 5.5);
    }
  }

  public static alignDEReplacementContractValues(
    filledValues: object,
    qsSuitabilityResponse: QsAPIGetQuestionnaireResponse
  ) {
    const replacedContracts = SuitabilityPayloadUtils.getReplacedContracts(qsSuitabilityResponse);

    for (let i = 0; i < replacedContracts.length; i++) {
      truncateLineTextBoxValue(filledValues, `replacementForm.contract_${i + 1}_insurer`, 27, 187, 5.5);
      truncateLineTextBoxValue(filledValues, `replacementForm.contract_${i + 1}_number`, 198, 309, 5.5);
      truncateLineTextBoxValue(filledValues, `replacementForm.contract_${i + 1}_face_amount`, 317, 407, 5.5);
      truncateLineTextBoxValue(filledValues, `replacementForm.contract_${i + 1}_annuitant.fullName`, 412, 504, 5.5);
    }
  }

  public static alignMNReplacementContractValues(
    filledValues: object,
    qsSuitabilityResponse: QsAPIGetQuestionnaireResponse
  ) {
    const replacedContracts = SuitabilityPayloadUtils.getReplacedContracts(qsSuitabilityResponse);

    for (let i = 0; i < replacedContracts.length; i++) {
      truncateLineTextBoxValue(filledValues, `replacementForm.contract_${i + 1}_insurer`, 27, 184, 5.5);
      truncateLineTextBoxValue(filledValues, `replacementForm.contract_${i + 1}_annuitant.fullName`, 196, 352, 5.5);
      truncateLineTextBoxValue(filledValues, `replacementForm.contract_${i + 1}_number`, 361, 431, 5.5);
      truncateLineTextBoxValue(filledValues, `replacementForm.contract_${i + 1}_type_of_policy`, 516, 586, 5.5);
    }
  }

  public static alignWYReplacementContractValues(
    filledValues: object,
    qsSuitabilityResponse: QsAPIGetQuestionnaireResponse
  ) {
    const replacedContracts = SuitabilityPayloadUtils.getReplacedContracts(qsSuitabilityResponse);

    for (let i = 0; i < replacedContracts.length; i++) {
      truncateLineTextBoxValue(filledValues, `replacementForm.contract_${i + 1}_insurer`, 36, 326, 5.5);
      truncateLineTextBoxValue(filledValues, `replacementForm.contract_${i + 1}_number`, 326, 444, 5.5);
      truncateLineTextBoxValue(filledValues, `replacementForm.contract_${i + 1}_annuitant.fullName`, 445, 558, 5.5);
    }
  }

  public static alignINReplacementContractValues(
    filledValues: object,
    qsSuitabilityResponse: QsAPIGetQuestionnaireResponse
  ) {
    const replacedContracts = SuitabilityPayloadUtils.getReplacedContracts(qsSuitabilityResponse);

    for (let i = 0; i < replacedContracts.length; i++) {
      truncateLineTextBoxValue(filledValues, `replacementForm.contract_${i + 1}_insurer`, 27, 114, 5.5);
      truncateLineTextBoxValue(filledValues, `replacementForm.contract_${i + 1}_type_of_policy`, 122, 210, 5.5);
      truncateLineTextBoxValue(filledValues, `replacementForm.contract_${i + 1}_number`, 216, 305, 5.5);
      truncateLineTextBoxValue(filledValues, `replacementForm.contract_${i + 1}_face_amount`, 406, 494, 5.5);
      truncateLineTextBoxValue(
        filledValues,
        `replacementForm.contract_${i + 1}_type_of_optional_benefits`,
        503,
        589,
        5.5
      );
    }
  }

  public static alignMAReplacementContractValues(
    filledValues: object,
    qsSuitabilityResponse: QsAPIGetQuestionnaireResponse
  ) {
    const replacedContracts = SuitabilityPayloadUtils.getReplacedContracts(qsSuitabilityResponse);

    for (let i = 0; i < replacedContracts.length; i++) {
      truncateLineTextBoxValue(filledValues, `replacementForm.contract_${i + 1}_annuitant.fullName`, 27, 150, 5.5);
      truncateLineTextBoxValue(filledValues, `replacementForm.contract_${i + 1}_insurer`, 158, 308, 5.5);
      truncateLineTextBoxValue(filledValues, `replacementForm.contract_${i + 1}_number`, 317, 407, 5.5);
      truncateLineTextBoxValue(filledValues, `replacementForm.contract_${i + 1}_face_amount`, 412, 501, 5.5);
      truncateLineTextBoxValue(filledValues, `replacementForm.contract_${i + 1}_plan_name`, 509, 586, 5.5);
    }
  }

  public static alignILReplacementContractValues(
    filledValues: object,
    qsSuitabilityResponse: QsAPIGetQuestionnaireResponse
  ) {
    const replacedContracts = SuitabilityPayloadUtils.getReplacedContracts(qsSuitabilityResponse);

    for (let i = 0; i < replacedContracts.length; i++) {
      truncateLineTextBoxValue(filledValues, `replacementForm.contract_${i + 1}_annuitant.fullName`, 27, 132, 5.5);
      truncateLineTextBoxValue(filledValues, `replacementForm.contract_${i + 1}_insurer`, 158, 288, 5.5);
      truncateLineTextBoxValue(filledValues, `replacementForm.contract_${i + 1}_number`, 317, 395, 5.5);
      truncateLineTextBoxValue(filledValues, `replacementForm.contract_${i + 1}_face_amount`, 412, 490, 5.5);
      truncateLineTextBoxValue(filledValues, `replacementForm.contract_${i + 1}_plan_name`, 509, 575, 5.5);
    }
  }

  public static alignIDReplacementContractValues(
    filledValues: object,
    qsSuitabilityResponse: QsAPIGetQuestionnaireResponse
  ) {
    const replacedContracts = SuitabilityPayloadUtils.getReplacedContracts(qsSuitabilityResponse);

    for (let i = 0; i < replacedContracts.length; i++) {
      truncateLineTextBoxValue(filledValues, `replacementForm.contract_${i + 1}_annuitant.fullName`, 158, 280, 5.5);
      truncateLineTextBoxValue(filledValues, `replacementForm.contract_${i + 1}_insurer`, 27, 133, 5.5);
      truncateLineTextBoxValue(filledValues, `replacementForm.contract_${i + 1}_number`, 316, 391, 5.5);
      truncateLineTextBoxValue(filledValues, `replacementForm.contract_${i + 1}_face_amount`, 509, 571, 5.5);
      truncateLineTextBoxValue(filledValues, `replacementForm.contract_${i + 1}_type_of_policy`, 412, 486, 5.5);
    }
  }

  public static align809ReplacementContractValues(
    filledValues: object,
    qsSuitabilityResponse: QsAPIGetQuestionnaireResponse
  ) {
    const replacedContracts = SuitabilityPayloadUtils.getReplacedContracts(qsSuitabilityResponse);

    for (let i = 0; i < replacedContracts.length; i++) {
      truncateLineTextBoxValue(filledValues, `replacementForm.contract_${i + 1}_insurer`, 51, 226, 5.5);
      truncateLineTextBoxValue(filledValues, `replacementForm.contract_${i + 1}_number`, 227, 319, 5.5);
      truncateLineTextBoxValue(filledValues, `replacementForm.contract_${i + 1}_annuitant.fullName`, 320, 503, 5.5);
    }
  }

  public static alignKSReplacementContractValues(
    filledValues: object,
    qsSuitabilityResponse: QsAPIGetQuestionnaireResponse
  ) {
    const replacedContracts = SuitabilityPayloadUtils.getReplacedContracts(qsSuitabilityResponse);

    for (let i = 0; i < replacedContracts.length; i++) {
      if (filledValues[`replacementForm.contract_${i + 1}_insurer.external`] !== undefined) {
        truncateLineTextBoxValue(
          filledValues,
          `replacementForm.contract_${i + 1}_annuitant.fullName.external`,
          36,
          158,
          5.5
        );
        truncateLineTextBoxValue(filledValues, `replacementForm.contract_${i + 1}_insurer.external`, 36, 159, 5.5);
        truncateLineTextBoxValue(
          filledValues,
          `replacementForm.contract_${i + 1}_annuitant.fullName.external`,
          166,
          318,
          5.5
        );
        truncateLineTextBoxValue(filledValues, `replacementForm.contract_${i + 1}_number.external`, 325, 414, 5.5);
        truncateLineTextBoxValue(filledValues, `replacementForm.contract_${i + 1}_face_amount.external`, 419, 509, 5.5);
        truncateLineTextBoxValue(
          filledValues,
          `replacementForm.contract_${i + 1}_type_of_policy.external`,
          518,
          577,
          5.5
        );
      }

      if (filledValues[`replacementForm.contract_${i + 1}_insurer.internal`] !== undefined) {
        truncateLineTextBoxValue(
          filledValues,
          `replacementForm.contract_${i + 1}_annuitant.fullName.internal`,
          36,
          158,
          5.5
        );
        truncateLineTextBoxValue(filledValues, `replacementForm.contract_${i + 1}_insurer.internal`, 36, 159, 5.5);
        truncateLineTextBoxValue(
          filledValues,
          `replacementForm.contract_${i + 1}_annuitant.fullName.internal`,
          166,
          318,
          5.5
        );
        truncateLineTextBoxValue(filledValues, `replacementForm.contract_${i + 1}_number.internal`, 325, 414, 5.5);
        truncateLineTextBoxValue(filledValues, `replacementForm.contract_${i + 1}_face_amount.internal`, 419, 509, 5.5);
        truncateLineTextBoxValue(
          filledValues,
          `replacementForm.contract_${i + 1}_type_of_policy.internal`,
          518,
          577,
          5.5
        );
      }
    }
  }

  public static addSuitabilityValuesForReplacementPDF(
    filledValues: object,
    qsSuitabilityResponse: QsAPIGetQuestionnaireResponse
  ) {
    const isAReplacemement = SuitabilityPayloadUtils.isReplacement(qsSuitabilityResponse);

    filledValues['appliedForAnnuity.is_this_a_replacement'] = isAReplacemement;

    const isAReplacementYesOrNo = isAReplacemement ? 'Yes' : 'No';
    filledValues[`appliedForAnnuity.is_this_a_replacement:${isAReplacementYesOrNo}`] = 'X';
  }

  public static addApplicationValuesForReplacementPDF(
    filledValues: object,
    unpersistedAnnuityWithApplicationValues: CoverpathAnnuity
  ) {
    filledValues['appliedForAnnuity.product_code'] = unpersistedAnnuityWithApplicationValues.product;
    filledValues['appliedForAnnuity.product_name'] = unpersistedAnnuityWithApplicationValues.getProductName();

    const carrier = 'CAS';
    filledValues['appliedForAnnuity.carrier'] = carrier;
    filledValues[`appliedForAnnuity.carrier:${carrier}`] = 'X';

    filledValues['appliedForAnnuity.initial_purchase_amount'] =
      unpersistedAnnuityWithApplicationValues.initialPurchaseAmount;

    filledValues['appliedForAnnuity.insurer'] = 'Massachusetts Mutual Life Insurance Company';
    filledValues['appliedForAnnuity.product_type'] = 'Deferred Fixed Annuity';
  }

  public static add6487ValuesForReplacementPDF(filledValues: object) {
    const replacementReason = filledValues['replacementForm.F6487-US--replacement_reason'];
    if (replacementReason && replacementReason.length !== 0) {
      filledValues['replacementForm.F6487-US--replacement_reason.v1'] = replacementReason;
      addMultiLineTextBoxValues(filledValues, 'replacementForm.F6487-US--replacement_reason.v1', 254, 520, 5.5);

      const replaceMentReasonLineOne = filledValues['replacementForm.F6487-US--replacement_reason.v1.line1'];
      const replacementReasonLineTwoRemainder = replacementReason.substring(replaceMentReasonLineOne.length);

      if (replacementReasonLineTwoRemainder && replacementReasonLineTwoRemainder.length !== 0) {
        filledValues['replacementForm.F6487-US--replacement_reason.v2'] = replacementReasonLineTwoRemainder;
        addMultiLineTextBoxValues(filledValues, 'replacementForm.F6487-US--replacement_reason.v2', 40, 520, 5.5);
      }
    }

    const inCompliance = filledValues['replacementForm.F6487-US--in_compliance'];
    filledValues[`replacementForm.F6487-US--in_compliance:${inCompliance}`] = 'X';
    const inComplianceDetails = filledValues['replacementForm.F6487-US--in_compliance_details'];
    filledValues[`replacementForm.F6487-US--in_compliance_details:${inCompliance}`] = inComplianceDetails;
    addMultiLineTextBoxValues(filledValues, 'replacementForm.F6487-US--in_compliance_details:No', 40, 520);

    const discontinuingPremiums = filledValues['replacementForm.F6487-US--discontinuing_premiums'];
    filledValues[`replacementForm.F6487-US--discontinuing_premiums:${discontinuingPremiums}`] = 'X';

    const usingFunds = filledValues['replacementForm.F6487-US--using_funds'];
    filledValues[`replacementForm.F6487-US--using_funds:${usingFunds}`] = 'X';
  }

  public static add6487_809ValuesForReplacementPDF(filledValues: object) {
    const inCompliance = filledValues['replacementForm.F6487_809--in_compliance'];
    filledValues[`replacementForm.F6487_809--in_compliance:${inCompliance}`] = 'X';

    const inComplianceDetails = filledValues['replacementForm.F6487_809--in_compliance_details'];
    filledValues[`replacementForm.F6487_809--in_compliance_details:${inCompliance}`] = inComplianceDetails;

    addMultiLineTextBoxValues(filledValues, 'replacementForm.F6487_809--in_compliance_details:No', 52, 576);

    const discontinuingPremiums = filledValues['replacementForm.F6487_809--discontinuing_premiums'];
    filledValues[`replacementForm.F6487_809--discontinuing_premiums:${discontinuingPremiums}`] = 'X';

    const usingFunds = filledValues['replacementForm.F6487_809--using_funds'];
    filledValues[`replacementForm.F6487_809--using_funds:${usingFunds}`] = 'X';

    const replacementReason = filledValues['replacementForm.F6487_809-replacement_reason'];
    if (replacementReason && replacementReason.length !== 0) {
      filledValues['replacementForm.F6487_809-replacement_reason.v1'] = replacementReason;
      addMultiLineTextBoxValues(filledValues, 'replacementForm.F6487_809-replacement_reason.v1', 268, 576, 5.5);

      const replaceMentReasonLineOne = filledValues['replacementForm.F6487_809-replacement_reason.v1.line1'];
      const replacementReasonLineTwoRemainder = replacementReason.substring(replaceMentReasonLineOne.length);

      if (replacementReasonLineTwoRemainder && replacementReasonLineTwoRemainder.length !== 0) {
        filledValues['replacementForm.F6487_809-replacement_reason.v2'] = replacementReasonLineTwoRemainder;
        addMultiLineTextBoxValues(filledValues, 'replacementForm.F6487_809-replacement_reason.v2', 37, 576, 5.5);
      }
    }
  }

  public static addReplacementContractValuesForReplacementPDF(
    filledValues: object,
    qsSuitabilityResponse: QsAPIGetQuestionnaireResponse
  ) {
    const replacedContracts = SuitabilityPayloadUtils.getReplacedContracts(qsSuitabilityResponse);

    for (let i = 0; i < replacedContracts.length; i++) {
      const replacedContract = replacedContracts[i];
      filledValues[`replacementForm.contract_${i + 1}_death_benefit`] = replacedContract.deathBenefit;

      const replacedOrFinanced = filledValues[`replacementForm.contract_${i + 1}_replaced_or_financed`];
      filledValues[`replacementForm.contract_${i + 1}_replaced_or_financed:${replacedOrFinanced}`] = 'X';

      const addressZip = filledValues[`replacementForm.contract_${i + 1}_insurer_address.zip`]
        ? filledValues[`replacementForm.contract_${i + 1}_insurer_address.zip`]
        : '';
      const addressLine1 = filledValues[`replacementForm.contract_${i + 1}_insurer_address.line1`]
        ? filledValues[`replacementForm.contract_${i + 1}_insurer_address.line1`]
        : '';
      const addressLine2 = filledValues[`replacementForm.contract_${i + 1}_insurer_address.line2`]
        ? filledValues[`replacementForm.contract_${i + 1}_insurer_address.line2`]
        : '';
      const addressState = filledValues[`replacementForm.contract_${i + 1}_insurer_address.state`]
        ? filledValues[`replacementForm.contract_${i + 1}_insurer_address.state`]
        : '';
      filledValues[`replacementForm.contract_${i + 1}_insurer_address.address.street_unit`] =
        addressLine1 + ' ' + addressLine2;

      const firstName = filledValues[`replacementForm.contract_${i + 1}_annuitant.firstName`];
      const middleName = filledValues[`replacementForm.contract_${i + 1}_annuitant.middleName`];
      const lastName = filledValues[`replacementForm.contract_${i + 1}_annuitant.lastName`];
      const fullName = firstName + ' ' + (middleName ? middleName + ' ' + lastName : lastName);
      filledValues[`replacementForm.contract_${i + 1}_annuitant.fullName`] = fullName;
    }
  }

  public static addOwnerValuesForReplacementPDF(
    filledValues: object,
    unpersistedAnnuityWithApplicationValues: CoverpathAnnuity
  ) {
    const ownerFullName = unpersistedAnnuityWithApplicationValues.owner.getFullNameOfOwner();

    filledValues['appliedForAnnuity.owner_full_name'] = ownerFullName;

    filledValues['appliedForAnnuity.owner_initials'] = ownerFullName
      .split(/\s/)
      .reduce((initials, name) => (initials += name.slice(0, 1) + '.'), '');

    filledValues['appliedForAnnuity.owner_legal_address_combined'] =
      unpersistedAnnuityWithApplicationValues.owner.legalAddress.getOneLineValue();

    filledValues['appliedForAnnuity.owner_legal_address_line1'] =
      unpersistedAnnuityWithApplicationValues.owner.legalAddress.getLine1();

    filledValues['appliedForAnnuity.owner_legal_address_line2'] =
      unpersistedAnnuityWithApplicationValues.owner.legalAddress.getLine2();

    filledValues['appliedForAnnuity.owner_legal_address_street'] =
      unpersistedAnnuityWithApplicationValues.owner.legalAddress.street;
    filledValues['appliedForAnnuity.owner_legal_address_city'] =
      unpersistedAnnuityWithApplicationValues.owner.legalAddress.city;
    filledValues['appliedForAnnuity.owner_legal_address_state'] =
      unpersistedAnnuityWithApplicationValues.owner.legalAddress.state;
    filledValues['appliedForAnnuity.owner_legal_address_zip'] =
      unpersistedAnnuityWithApplicationValues.owner.legalAddress.zip;
    filledValues['appliedForAnnuity.owner_phone_number'] =
      unpersistedAnnuityWithApplicationValues.owner.phone.getFormatted();

    filledValues['appliedForAnnuity.joint_owner_full_name'] =
      unpersistedAnnuityWithApplicationValues.getOtherOwnerName();
    filledValues['appliedForAnnuity.primary_annuitant_full_name'] =
      unpersistedAnnuityWithApplicationValues.annuitant.getFullName();
  }

  public static addProducerValuesForReplacementPDF(
    filledValues: object,
    primaryPolicyAgent: PolicyAgent,
    producerLicense: AgentLicense
  ) {
    let licenseNumber = producerLicense ? producerLicense.licenseNumber : '';

    if (!licenseNumber && Utils.isTestingEnv()) {
      LoggerSingleton.getInstance().info(
        {
          module: 'AnnuityPDFUtils',
          function: 'addProducerValuesForReplacementPDF',
          category: Category.request,
        },
        `Using mocked agent license number since none was available`
      );

      licenseNumber = '00000000';
    }

    filledValues['appliedForAnnuity.producer_license_number'] = licenseNumber;

    filledValues['appliedForAnnuity.producer_full_name'] = primaryPolicyAgent.agent.person.getFullNameOfOwner();
    filledValues['appliedForAnnuity.producer_address_combined'] = primaryPolicyAgent.agent.address.getOneLineValue();
    filledValues['appliedForAnnuity.producer_address_line1'] = primaryPolicyAgent.agent.address.getLine1();
    filledValues['appliedForAnnuity.producer_address_line2'] = primaryPolicyAgent.agent.address.getLine2();
    filledValues['appliedForAnnuity.producer_address_street'] = primaryPolicyAgent.agent.address.street;
    filledValues['appliedForAnnuity.producer_address_city'] = primaryPolicyAgent.agent.address.city;
    filledValues['appliedForAnnuity.producer_address_state'] = primaryPolicyAgent.agent.address.state;
    filledValues['appliedForAnnuity.producer_address_zip'] = primaryPolicyAgent.agent.address.zip;
    filledValues['appliedForAnnuity.producer_phone_number'] = primaryPolicyAgent.agent.person.phone.toString();
  }
}

export class ProductDisclosureValuesPDFTransformer {
  public static addDynamicValuesForPDF(values: object) {
    const ownerName = this.addOwnerName(values);
    const address = this.addOwnerAddress(values);
    const taxId = this.addOwnerTaxId(values);

    return {
      ...ownerName,
      ...address,
      ...taxId,
    };
  }

  public static addOwnerName(values: object) {
    let ownerName = '',
      jointOwnerName = '';
    const ownership = values['owner.primary_ownership'];
    if (ownership === 'individual') {
      const firstName = values['owner.name.firstName'];
      const middleName = values['owner.name.middleName'];
      const lastName = values['owner.name.lastName'];
      ownerName = getFullName({ firstName, middleName, lastName });
    } else {
      ownerName = values['owner.name_entity'];
    }

    const is_there_a_joint_owner = values['owner.is_there_a_joint_owner'];
    if (is_there_a_joint_owner === 'Yes') {
      const firstName = values['owner.joint_owner_details.name.firstName'];
      const middleName = values['owner.joint_owner_details.name.middleName'];
      const lastName = values['owner.joint_owner_details.name.lastName'];
      jointOwnerName = getFullName({ firstName, middleName, lastName });
    }

    const name = is_there_a_joint_owner === 'Yes' ? `${ownerName}, ${jointOwnerName}` : ownerName;
    return { 'owner.name': name };
  }

  public static addOwnerAddress(values: object) {
    const streetAddress = `${values['owner.address_legal.line1']} ${values['owner.address_legal.line2'] || ''}`;
    const city = values['owner.address_legal.city'];
    const state = values['owner.address_legal.state'];
    const zip = values['owner.address_legal.zip'];

    return {
      'owner.address.line1': streetAddress,
      'owner.address.city': city,
      'owner.address.state': state,
      'owner.address.zip': zip,
    };
  }

  public static addOwnerTaxId(values: object) {
    const is_there_a_joint_owner = values['owner.is_there_a_joint_owner'];
    const taxId =
      is_there_a_joint_owner === 'Yes'
        ? `${values['owner.tax_ID']}, ${values['owner.joint_owner_details.tax_ID']}`
        : values['owner.tax_ID'];
    return { 'owner.taxId': taxId };
  }
}

export const CONDITIONAL_PDF_VALUES_TRANSFORMER_MAP = {
  [POLICY_DOC_DOCTYPE.annuityMM2Disclosure]: MM2DisclosureValuesPDFTransformer.addDynamicValuesForPDF,
  [POLICY_DOC_DOCTYPE.annuityQLACForm]: QLACValuesPDFTransformer.addDynamicValuesForPDF,
  [POLICY_DOC_DOCTYPE.annuityLargeCaseQuestionnaire]: LargeCaseQuestionnaireValuesPDFTransformer.addDynamicValuesForPDF,
  [POLICY_DOC_DOCTYPE.annuityDisclosureAcknowledgment]:
    DisclosureAcknowledgmentValuesPDFTransformer.addDynamicValuesForPDF,
};
